import { customElement } from 'lit/decorators.js';
import { defaultStagedThreshold, EventCancellation } from '../../../../webmodule-common/other/general/staged-event';
import { EventGetReference, EventSnippet, EventTemplate } from '../../../../webmodule-common/other/ui/events';
import {
  FabricationPurchaseOrderState,
  FabricationPurchaseOrderStateCount,
  PurchaseOrderSummaryView
} from '../../../api/dealer-api-interface-all';
import { FabricationPurchaseOrderSummaryTable } from './fabrication-purchase-order-summary-table';
import { fireQuickSuccessToast } from '../../../../webmodule-common/other/toast-away';
import { getApiFactory } from '../../../api/api-injector';
import { getOnlineSupplierOrWarn } from '../../../fabrication-purchase-orders/ui/supplier-guard';
import { html } from 'lit';
import { IconRefresh } from '../../../../webmodule-common/other/ui/icons/icon-refresh';
import { LangFabricationPurchaseOrder } from '../../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';
import { launchFabricationPurchaseOrder } from '../../../fabrication-purchase-orders/ui/launcher';
import { newGuid } from '../../../../webmodule-common/other/api/guid';
import { notificationSignal } from '../../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { TabManager } from '../../../../webmodule-common/other/ui/tab-manager/tab-manager';
import type { TabPage } from '../../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { stagedExecution } from '../../../../webmodule-common/other/general/event-source-client';
import { ViewBase } from '../../../../webmodule-common/other/ui/view-base';
import { WMEventSource } from '../../../api/event-source';

export interface FabricationPurchaseOrderListViewOptions {
  branchId: EventGetReference;
}

@customElement('wm-fabricationpurchaseorderlistview')
export class FabricationPurchaseOrderListView extends ViewBase {
  protected readonly branchId: EventGetReference;
  protected tabManager: TabManager;
  private readonly elementId: string;
  protected tables: FabricationPurchaseOrderSummaryTable[];
  protected stateCounts: FabricationPurchaseOrderStateCount[] = [];

  constructor(options: FabricationPurchaseOrderListViewOptions) {
    super();
    this.elementId = `fabrication-purchase-order-list-view-${newGuid()}`;
    this.branchId = options.branchId;
    this.tables = this.initializeTables();
    this.tabManager = this.createTabManager();
  }

  public async setActiveTabByHash() {
    await this.tabManager?.applyWindowHash();
  }

  public async afterConstruction(): Promise<void> {
    await this.updatePageTotals();
    const table = this.tabManager.activePage?.data as FabricationPurchaseOrderSummaryTable;
    if (table) await table.refreshData(1);
  }

  protected createTabManager(): TabManager {
    const getInitialPages = (): TabPage[] => {
      return this.tables.map(table => {
        const page: TabPage = {
          caption: table.eventTitle,
          onEnter: () => {
            // enable data load the first time we enter the tab, but not before
            table.delayedDataLoad = false;
            // not awaited on purpose, for a faster tab switch; safe to do
            if (table.dataIsStale) table.refreshData();
          },
          content: () => table,
          data: table,
          fragment: table.pageFragment
        };
        return page;
      });
    };

    return new TabManager({
      initialIndex: 0,
      addAction: {
        caption: () => LangFabricationPurchaseOrder.newPoButton(),
        onClick: async () => {
          await this.createEmptyPurchaseOrder();
        }
      },
      actions: [
        {
          caption: () => html`${new IconRefresh()}`,
          onClick: async () => {
            const table = this.tabManager.activePage?.data as FabricationPurchaseOrderSummaryTable | undefined;
            if (!table) return;
            await table.refreshData();
            this.tables.forEach(t => {
              if (t !== table) t.dataIsStale = true;
            });
          }
        }
      ],
      pages: () => getInitialPages()
    });
  }

  protected async updatePageTotals() {
    const result = await getApiFactory()
      .fabricationPurchaseOrder()
      .getPurchaseOrderTotalsByState({ branchId: await this.branchId() });
    if (result) this.stateCounts = result.stateCounts;
    this.tabManager.requestUpdate();
  }

  protected getCount(...states: FabricationPurchaseOrderState[]): number {
    return this.stateCounts.filter(sc => states.includes(sc.state)).reduce((sum, sc) => sum + sc.count, 0);
  }

  protected initializeTables(): FabricationPurchaseOrderSummaryTable[] {
    const makeTable = (titleStr: () => string, states: FabricationPurchaseOrderState[], pageFragment: string) => {
      const title: EventSnippet = () => {
        const count = this.getCount(...states);
        return html`${titleStr()} (${count})`;
      };
      return new FabricationPurchaseOrderSummaryTable({
        states,
        title,
        branchId: this.branchId,
        pageFragment,
        cancelEvent: async po => await this.cancelPurchaseOrder(po)
      });
    };

    return [
      makeTable(() => LangFabricationPurchaseOrder.draftTab(), [FabricationPurchaseOrderState.Draft], 'draft'),
      makeTable(
        () => LangFabricationPurchaseOrder.issuedTab(),
        // IssuedPending is transient (the async issue flow completes in seconds-to-a-minute);
        // surface a just-issued PO under Issued with its own "Issuing" badge until it lands on Issued.
        [FabricationPurchaseOrderState.Issued, FabricationPurchaseOrderState.IssuedPending],
        'issued'
      ),
      makeTable(
        () => LangFabricationPurchaseOrder.completedTab(),
        [FabricationPurchaseOrderState.Completed],
        'completed'
      ),
      makeTable(
        () => LangFabricationPurchaseOrder.cancelledTab(),
        [FabricationPurchaseOrderState.Cancelled],
        'cancelled'
      )
    ];
  }

  protected async createEmptyPurchaseOrder(): Promise<void> {
    const supplier = await getOnlineSupplierOrWarn();
    if (!supplier) return;

    const result = await getApiFactory()
      .fabricationPurchaseOrder()
      .createEmptyPurchaseOrder({
        branchId: await this.branchId(),
        supplierId: supplier.supplierId,
        title: LangFabricationPurchaseOrder.newPoDefaultTitle(),
        description: ''
      });

    if (result) {
      await launchFabricationPurchaseOrder(result.purchaseOrder.id);
    }
  }

  protected async cancelPurchaseOrder(po: PurchaseOrderSummaryView): Promise<void> {
    const result = await getApiFactory()
      .fabricationPurchaseOrder()
      .cancelPurchaseOrder({ id: po.id, recordVersion: po.recordVersion });
    if (result) {
      await this.updatePageTotals();
      const table = this.tabManager.activePage?.data as FabricationPurchaseOrderSummaryTable;
      if (table) await table.refreshData();
      this.tables.forEach(t => {
        if (t !== table) t.dataIsStale = true;
      });
      fireQuickSuccessToast(LangFabricationPurchaseOrder.cancelledToast());
    }
  }

  private _eventCancel: EventCancellation = new EventCancellation();

  // A state change moves the order between tabs (most visibly when the async issue flow
  // takes it off the "Issuing" badge), so refresh the totals and reload the visible tab -
  // the event does not say which tab the order left.
  private _eventPurchaseOrderUpdated = stagedExecution({
    threshold: defaultStagedThreshold,
    cancelToken: this._eventCancel,
    eventFinally: () => notificationSignal(false),
    event: async () => {
      await this.updatePageTotals();
      this.tables.forEach(t => (t.dataIsStale = true));
      const table = this.tabManager.activePage?.data as FabricationPurchaseOrderSummaryTable;
      if (table) {
        await table.refreshData();
        table.dataIsStale = false;
      }
    },
    eventTriggered: () => {
      notificationSignal(true);
    }
  });

  async dispose(): Promise<void> {
    await super.dispose();
    this._eventCancel.cancel();
  }

  connectedCallback(): void {
    super.connectedCallback();
    WMEventSource.getInstance().addEventListener(
      WMEventSource.fabricationPurchaseOrder,
      this._eventPurchaseOrderUpdated
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    WMEventSource.getInstance().removeEventListener(
      WMEventSource.fabricationPurchaseOrder,
      this._eventPurchaseOrderUpdated
    );
  }

  protected template(): EventTemplate {
    return html`<div id=${this.elementId} class="page-content">${this.tabManager?.ui}</div>`;
  }
}
